import React, { useState, useRef, useEffect } from 'react';
import { MoreVertical, ChevronDown } from 'lucide-react';

export interface ActionMenuItem {
    label: string;
    icon: React.ComponentType<{ size?: number; className?: string }>;
    onClick: () => void;
    color?: string;
    badge?: string | number;
    disabled?: boolean;
}

interface TableActionMenuProps {
    items: ActionMenuItem[];
    label?: string;
    align?: 'right' | 'left';
}

const TableActionMenu: React.FC<TableActionMenuProps> = ({
    items,
    label = 'Actions',
    align = 'right'
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    return (
        <div className="relative inline-block text-left" ref={menuRef}>
            <button
                type="button"
                onClick={() => setIsOpen(prev => !prev)}
                className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition-all cursor-pointer ${
                    isOpen 
                        ? 'bg-slate-100 text-slate-900 border-slate-300 shadow-inner' 
                        : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50 hover:text-slate-900 hover:border-slate-300 shadow-2xs'
                }`}
                title="Options et actions supplémentaires"
            >
                <span>{label}</span>
                <ChevronDown size={13} className={`text-slate-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div 
                    className={`absolute z-40 mt-1 w-44 rounded-xl bg-white shadow-xl border border-slate-100 py-1.5 animate-in fade-in zoom-in-95 duration-100 ${
                        align === 'right' ? 'right-0' : 'left-0'
                    }`}
                >
                    {items.map((item, idx) => {
                        const Icon = item.icon;
                        return (
                            <button
                                key={idx}
                                type="button"
                                disabled={item.disabled}
                                onClick={() => {
                                    setIsOpen(false);
                                    item.onClick();
                                }}
                                className={`w-full flex items-center justify-between px-3.5 py-2 text-xs font-medium transition-colors text-left cursor-pointer ${
                                    item.disabled 
                                        ? 'opacity-40 cursor-not-allowed text-gray-400' 
                                        : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'
                                }`}
                            >
                                <div className="flex items-center gap-2.5">
                                    <Icon size={14} className={item.color || 'text-slate-500'} />
                                    <span>{item.label}</span>
                                </div>
                                {item.badge !== undefined && (
                                    <span className="px-1.5 py-0.2 rounded-full text-[10px] font-bold bg-blue-100 text-blue-700">
                                        {item.badge}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default TableActionMenu;

